<script lang="ts">
    import tabIcon from "@/assets/gear.svg";

    export default {
        name: "ListMaterialgrupp",
        tabName: "Materialgrupper",
        icon: tabIcon,
    };
</script>

<script setup lang="ts">
    import CollapsibleSection from "@/components/generic/CollapsibleSection.vue";
    import MaterialgruppForm from "@/tabs/Admin/forms/MaterialgruppForm.vue";
    import MaterialCell from "@/tabs/Admin/ListMaterialgrupp/MaterialCell.vue";
    import { ref, onBeforeUnmount, onBeforeMount, computed, toRefs } from "vue";
    import { openDialog } from "@/utils";
    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import KlinikenTable from "@/components/Table/KlinikenTable.vue";
    import CustomButton from "@/components/Button.vue";
    import { useStore } from "@/store";

    // TODO Ändra till användning av type i materialgruppModule i Vue3
    interface Materialgrupp {
        id: string;
        gruppnummer: string;
        namn: string;
        streckkod: string;
        material: {
            antal: number;
            material: string;
        }[];
    }

    const store = useStore();

    const props = defineProps<{
        tabId: number;
    }>();

    const columns = [
        {
            title: "Namn",
            name: "namn",
            sort: "namn",
        },
        {
            title: "Gruppnummer",
            name: "gruppnummer",
            sort: "gruppnummer",
        },
        {
            title: "Streckkod",
            name: "streckkod",
            sort: "streckkod",
        },
        {
            title: "Material",
            name: "material",
            component: MaterialCell,
            cssClass: "material-table-cell",
        },
    ];
    const dropdownActions: {
        name: string;
        title: string;
        show: () => boolean;
    }[] = [
        {
            name: "editMaterialgrupp",
            title: "Redigera materialgrupp",
            show: () => true,
        },
    ];
    const listPage = ref<number>(1);
    const listCount = ref<number>(0);
    const listLimit = ref<number>(20);
    const materialgrupper = computed<Materialgrupp[]>(() =>
        store.getters["materialgrupp/materialgrupperSubset"]({
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        })
    );
    const nyaMaterialgrupper = computed<Materialgrupp[]>(() =>
        store.getters["materialgrupp/materialgrupperSubset"]({
            subsetName: "newlyCreated",
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        })
    );
    const redigeradeMaterialgrupper = computed<Materialgrupp[]>(() =>
        store.getters["materialgrupp/materialgrupperSubset"]({
            subsetName: "newlyUpdated",
            offset: listLimit.value * (listPage.value - 1),
            limit: listLimit.value,
        })
    );

    const { tabId } = toRefs(props);

    defineExpose({
        tabId,
    });

    const onChangeLimit = async (newLimit: number) => {
        listLimit.value = newLimit;

        await fetchMaterialgrupper({
            limit: newLimit,
            page: 1,
        });
    };

    const onLoadPage = async (page: number) => {
        await fetchMaterialgrupper({ page });
    };

    const openCreateMaterialgrupp = () => {
        store.dispatch("tabs/openTab", {
            component: MaterialgruppForm,
        });
    };

    const openEditMaterialgrupp = (materialgrupp: Materialgrupp) => {
        store.dispatch("tabs/openTab", {
            component: MaterialgruppForm,
            parameters: {
                materialgruppId: materialgrupp.id,
            },
        });
    };

    const fetchMaterialgrupper = async ({ page, limit = listLimit.value }: { page: number; limit?: number }) => {
        try {
            const { nextPage, total } = await store.dispatch("materialgrupp/fetchMaterialgrupper", {
                page,
                limit,
            });

            listPage.value = page;

            if (total) {
                listCount.value = total;
            }
        } catch (error) {
            console.error(error);

            openDialog("Kunde inte hämta materialgrupper", "error");
        }
    };

    onBeforeMount(async () => {
        await fetchMaterialgrupper({ page: listPage.value });
        await store.dispatch("material/addSubscriber");
        await store.dispatch("materialgrupp/addSubscriber");
        await store.dispatch("leverantor/addSubscriber");
    });

    onBeforeUnmount(async () => {
        await store.dispatch("material/removeSubscriber");
        await store.dispatch("materialgrupp/removeSubscriber");
        await store.dispatch("leverantor/removeSubscriber");
    });
</script>

<template>
    <div class="list-material-grupp">
        <admin-lock-border />
        <div class="header">
            <h1>Materialgrupper</h1>
            <CustomButton type="button" variant="hollow" @click="openCreateMaterialgrupp">
                <img src="@/assets/plus_circle_blue.svg" />
                <span>Skapa materialgrupp</span>
            </CustomButton>
        </div>
        <div class="newly" v-if="redigeradeMaterialgrupper.length > 0">
            <collapsible-section isSingle :isExpanded="false" heading="Nyligen redigerade materialgrupper">
                <kliniken-table
                    :items="redigeradeMaterialgrupper"
                    :columns="columns"
                    @editMaterialgrupp="openEditMaterialgrupp"
                    :has-dropdown="true"
                    :dropdown-actions="dropdownActions"
                />
            </collapsible-section>
        </div>
        <div class="newly-created" v-if="nyaMaterialgrupper.length > 0">
            <collapsible-section isSingle :isExpanded="false" heading="Nyligen tillagda materialgrupper">
                <kliniken-table
                    :items="nyaMaterialgrupper"
                    :columns="columns"
                    @editMaterialgrupp="openEditMaterialgrupp"
                    :has-dropdown="true"
                    :dropdown-actions="dropdownActions"
                />
            </collapsible-section>
        </div>

        <div>
            <h3 v-if="nyaMaterialgrupper.length > 0 || redigeradeMaterialgrupper.length > 0">Alla materialgrupper</h3>
            <kliniken-table
                :items="materialgrupper"
                :columns="columns"
                :count="listCount"
                :currentPage="listPage"
                :limit="listLimit"
                paginationStyle="paging"
                @loadPage="onLoadPage"
                @changeLimit="onChangeLimit"
                @editMaterialgrupp="openEditMaterialgrupp"
                @createMaterialgrupp="openCreateMaterialgrupp"
                :showBottom="true"
                :showLimitDropdown="true"
                :has-dropdown="true"
                :dropdown-actions="dropdownActions"
            />
        </div>
    </div>
</template>

<style lang="scss">
    .list-material-grupp {
        .kliniken-table {
            .kliniken-table-body {
                td {
                    align-content: baseline;
                }

                .material-table-cell {
                    width: 100%;
                    padding-top: 0;
                    padding-bottom: 0;

                    .kliniken-table-cell {
                        width: inherit;
                    }
                }
            }
        }

        .header {
            h1 {
                margin: 0;
            }

            display: flex;
            gap: 20px;
            align-items: center;
        }

        .newly {
            margin-top: 1rem;
        }
    }
</style>
